import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { useTheme } from "../../../theme/OAS365Theme";

/** Column layout shared by header row and data rows. */
const GRID_COLUMNS = "110px 1fr 200px";

function formatDate(dateStr) {
  if (!dateStr) return null;
  try {
    const raw = String(dateStr).trim();
    const d = new Date(raw);
    if (isNaN(d.getTime())) return dateStr;

    // Graph sends UTC (…Z); render in the browser's local timezone so the
    // times match what OneDrive shows the user.
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const day = d.getDate();
    const hour24 = d.getHours();
    const minute = d.getMinutes();

    const ampm = hour24 >= 12 ? "PM" : "AM";
    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
    const mm = String(minute).padStart(2, "0");
    const isoDate = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

    return `${isoDate}, ${hour12}:${mm} ${ampm}`;
  } catch {
    return dateStr;
  }
}

/** Read a value from a version record tolerating both camelCase and Graph nested shapes. */
function readVersion(row) {
  const versionId =
    row.versionId ?? row.VersionId ?? row.version ?? row.Version ?? row.id ?? row.Id ?? "–";
  const modified =
    row.lastModifiedDateTime ??
    row.LastModifiedDateTime ??
    row.modified ??
    row.Modified ??
    row.dateCreated ??
    row.DateCreated ??
    null;
  const modifiedBy =
    row.lastModifiedByName ??
    row.LastModifiedByName ??
    row.modifiedBy ??
    row.ModifiedBy ??
    row.lastModifiedBy?.user?.displayName ??
    row.LastModifiedBy?.User?.DisplayName ??
    row.lastModifiedBy?.displayName ??
    "–";
  return { versionId, modified, modifiedBy };
}

function VersionRow({ row, isLast, theme }) {
  const { versionId, modified, modifiedBy } = readVersion(row);
  const formatted = formatDate(modified);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: GRID_COLUMNS,
        gap: 0.75,
        alignItems: "flex-start",
        px: 0.5,
        width: "100%",
        pt: 0.5,
        pb: isLast ? 0.5 : 1,
        borderBottom: isLast
          ? "none"
          : `1px solid ${theme.colors?.dialogBorderColor || theme.colors?.borderColor || "#F0F0F0"}`,
      }}
    >
      {/* Version */}
      <Typography
        sx={{
          fontSize: "13px",
          fontWeight: 500,
          color: theme.colors?.fontColor,
          fontFamily: theme.fonts?.regular,
          pt: "4px",
          lineHeight: 1.25,
          textAlign: "center",
        }}
      >
        {versionId}
      </Typography>

      {/* Modified By */}
      <Tooltip title={modifiedBy} placement="top" arrow>
        <Typography
          sx={{
            fontSize: "13px",
            color: theme.colors?.fontColor,
            fontFamily: theme.fonts?.regular,
            pt: "4px",
            lineHeight: 1.25,
            minWidth: 0,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {modifiedBy}
        </Typography>
      </Tooltip>

      {/* Modified */}
      <Tooltip title={formatted ?? "–"} placement="top" arrow>
        <Typography
          sx={{
            fontSize: "13px",
            color: theme.colors?.fontColor,
            fontFamily: theme.fonts?.regular,
            pt: "4px",
            lineHeight: 1.25,
            whiteSpace: "nowrap",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {formatted ?? "–"}
        </Typography>
      </Tooltip>
    </Box>
  );
}

/**
 * Version history dialog for OneDrive/channel documents. Same chrome as the IFS
 * Document History dialog, with OneDrive-appropriate columns.
 */
export function OneDriveVersionHistoryDialog({
  isOpen,
  onClose,
  historyData = [],
  isLoading = false,
  error = null,
  document = null,
}) {
  const theme = useTheme();
  const docTitle = document?.name || document?.title || "Document";

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "min(94vw, 520px)",
          maxHeight: "82vh",
          borderRadius: "12px",
          backgroundColor:
            theme.colors?.dialogBackgroundColor || theme.colors?.pureColor,
          color: theme.colors?.fontColor,
          boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
        },
      }}
    >
      <Box
        sx={{
          height: 6,
          width: "100%",
          background:
            "linear-gradient(90deg, rgb(36, 159, 93) 0%, rgb(226, 239, 223) 98.79%)",
          flexShrink: 0,
        }}
      />

      {/* Header */}
      <DialogTitle
        sx={{
          pl: 2,
          pb: 1.25,
          pt: 1.5,
          pr: 2,
          borderBottom: `1px solid ${theme.colors?.dialogBorderColor || theme.colors?.borderColor || "#E0E0E0"}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          {/* Green icon */}
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "10px",
              backgroundColor:
                theme.colors?.themeDarkColor ||
                theme.colors?.themeColor ||
                "#3F9A52",
              border: `1px solid ${theme.colors?.borderColor || "rgba(0,0,0,0.08)"}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <HistoryOutlinedIcon
              sx={{
                fontSize: 26,
                color: theme.colors?.addovationWhite || "#fff",
              }}
            />
          </Box>
          {/* Title block */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 700,
                color: theme.colors?.fontColor,
                fontFamily: theme.fonts?.regular,
                lineHeight: 1.3,
              }}
            >
              OneDrive History
            </Typography>
            <Tooltip title={docTitle} placement="top" arrow>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: theme.colors?.fontColor,
                  opacity: 0.62,
                  fontFamily: theme.fonts?.regular,
                  mt: 0.25,
                  lineHeight: 1.35,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                  maxWidth: "100%",
                }}
              >
                {docTitle}
              </Typography>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>

      {/* Body */}
      <DialogContent
        sx={{
          px: 1,
          pt: 2,
          pb: 1,
          overflowX: "hidden",
          overflowY: "auto",
        }}
      >
        {isLoading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 220,
            }}
          >
            <CircularProgress sx={{ color: theme.colors?.themeColor || "#2E7D32" }} />
          </Box>
        )}
        {!isLoading && error && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 220,
            }}
          >
            <Typography sx={{ color: theme.colors?.errorColor || "#D92D20" }}>
              {error}
            </Typography>
          </Box>
        )}
        {!isLoading && !error && historyData.length === 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 220,
            }}
          >
            <Typography sx={{ color: theme.colors?.fontColor, opacity: 0.6 }}>
              No version history available for this document
            </Typography>
          </Box>
        )}
        {!isLoading && !error && historyData.length > 0 && (
          <Box sx={{ width: "100%", minWidth: 0 }}>
            {/* Column headers */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: GRID_COLUMNS,
                gap: 0.75,
                px: 0.5,
                py: 1,
                mb: 0.5,
                width: "100%",
                border: `1px solid ${theme.colors?.dialogBorderColor || theme.colors?.borderColor || "#E5E7EB"}`,
                borderRadius: "8px",
                backgroundColor: theme.colors?.tableHeaderBg || "#F9FAFB",
              }}
            >
              {["Version", "Modified By", "Modified"].map((col) => (
                <Typography
                  key={col}
                  sx={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: theme.colors?.fontColor,
                    fontFamily: theme.fonts?.regular,
                    textAlign: col === "Version" ? "center" : "left",
                  }}
                >
                  {col}
                </Typography>
              ))}
            </Box>
            {/* Rows */}
            <Box
              sx={{
                pt: 0.5,
                maxHeight: "48vh",
                overflowY: "auto",
                overflowX: "hidden",
                scrollbarWidth: "thin",
                scrollbarColor: `${theme.colors?.scrollbarThumb || "#ccc"} ${theme.colors?.scrollbarBackground || "transparent"}`,
                "&::-webkit-scrollbar": {
                  width: 10,
                },
                "&::-webkit-scrollbar-track": {
                  background: theme.colors?.scrollbarBackground || "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: theme.colors?.scrollbarThumb || "#ccc",
                  borderRadius: "10px",
                  border: `2px solid ${theme.colors?.scrollbarBackground || "transparent"}`,
                },
              }}
            >
              {historyData.map((row, index) => (
                <VersionRow
                  key={row.id ?? row.versionId ?? index}
                  row={row}
                  isLast={index === historyData.length - 1}
                  theme={theme}
                />
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      {/* Footer */}
      <Divider />
      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Typography
            sx={{
              fontSize: "13px",
              color: theme.colors?.fontColor,
              opacity: 0.75,
              fontFamily: theme.fonts?.regular,
            }}
          >
            {isLoading
              ? "Loading…"
              : `Showing ${historyData.length} version(s)`}
          </Typography>
        </Box>
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{
            borderColor: theme.colors?.dialogCloseBorder || "#57A77A",
            color: theme.colors?.dialogCloseText || "#57A77A",
            textTransform: "none",
            fontSize: "12px",
            fontWeight: 500,
            fontFamily: theme.fonts?.regular,
            borderRadius: "999px",
            minWidth: 66,
            height: 30,
            px: 2,
            backgroundColor: theme.colors?.dialogCloseBg || "#FFFFFF",
            "&:hover": {
              borderColor: theme.colors?.dialogCloseHoverBorder || "#4A9A6C",
              backgroundColor: theme.colors?.dialogCloseHoverBg || "#F7FCF8",
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default OneDriveVersionHistoryDialog;
